/*
 * lib/cache.js
 *
 * Local-first pijler 1: cache van het volledige state.S in localStorage.
 *
 * Bij elke succesvolle hydrate schrijven we de hele state-snapshot weg
 * onder een per-user key. Bij volgende app-start probeert main.js eerst
 * die snapshot toe te passen voor een instant render — daarna gaat de
 * echte hydrate in de achtergrond door en overschrijft de cache met
 * verse data.
 *
 * Versionering via VERSION-constant: caches met een andere versie worden
 * genegeerd zodat een schema-shift niet stiekem een halve oude state
 * binnenhaalt.
 */

import { state } from '../state.js';

const KEY_PREFIX = 'florijn:cache:';
const VERSION = 1;

function keyFor(userId) {
  return `${KEY_PREFIX}${userId}:v${VERSION}`;
}

/** Laad de cache voor één user. Returns null bij ontbreken/versie-mismatch/corrupt. */
export function loadCache(userId) {
  if (typeof localStorage === 'undefined' || !userId) return null;
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Dump de huidige state-snapshot naar localStorage. Fire-and-forget;
 *  bij quota-fout (localStorage vol) loggen we 't en gaan we verder. */
export function writeCache() {
  if (typeof localStorage === 'undefined' || !state.userId) return;
  try {
    const payload = {
      v:                VERSION,
      cachedAt:         Date.now(),
      userId:           state.userId,
      householdId:      state.householdId,
      householdName:    state.householdName,
      hasSharedAccount: state.hasSharedAccount,
      mateConnected:    state.mateConnected,
      user:             state.user,
      person:           state.person,
      displayNames:     state.displayNames,
      S:                state.S
    };
    localStorage.setItem(keyFor(state.userId), JSON.stringify(payload));
  } catch (err) {
    console.warn('Cache write failed (quota?):', err);
  }
}

/** Pas een geladen cache toe op de live state. Mutates state in place. */
export function applyCache(cached) {
  if (!cached) return;
  state.userId           = cached.userId           ?? null;
  state.householdId      = cached.householdId      ?? null;
  state.householdName    = cached.householdName    ?? '';
  state.hasSharedAccount = cached.hasSharedAccount ?? false;
  state.mateConnected    = cached.mateConnected    ?? false;
  state.user             = cached.user             ?? 'sven';
  state.person           = cached.person           ?? state.user;
  state.displayNames     = cached.displayNames     ?? state.displayNames;
  state.S                = cached.S                ?? state.S;
}

/** Wis alle florijn-caches (bv. bij sign-out zodat geen residue blijft). */
export function clearAllCaches() {
  if (typeof localStorage === 'undefined') return;
  try {
    Object.keys(localStorage).forEach(k => {
      if (k.startsWith(KEY_PREFIX)) localStorage.removeItem(k);
    });
  } catch {}
}
